import React, { useEffect, useState } from 'react';
import axios from '../axiosInstance';
import { FaEdit, FaPlus, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { exportBrandedPdf } from '../utils/brandedPdf';
import '../Pages.css';
import Pagination from '../components/Pagination';
import { formatDateTime } from '../utils/dateFormat';

function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('first_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    roles: [],
    organisation_id: ''
  });
  const [message, setMessage] = useState('');
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState({ show: false, userId: null, isActive: null });

  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchOrganisations();
    // Fetch current user info
    axios.get('/users/me').then(res => {
      setCurrentUser(res.data && res.data.user ? res.data.user : null);
    }).catch(() => setCurrentUser(null));
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/users');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching users', err);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.get('/roles');
      setRoles(res.data);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const fetchOrganisations = async () => {
    try {
      const res = await axios.get('/organisations');
      setOrganisations(res.data);
    } catch (err) {
      console.error('Error fetching organisations:', err);
    }
  };

  const handleSort = (col) => {
    const order = sortColumn === col && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortColumn(col);
    setSortOrder(order);
  };

  const filtered = searchTerm
    ? users.filter(u =>
        Object.values(u).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : users;

  const sorted = filtered.sort((a, b) => {
    const valA = (a[sortColumn] || '').toString().toLowerCase();
    const valB = (b[sortColumn] || '').toString().toLowerCase();
    return valA < valB ? (sortOrder === 'asc' ? -1 : 1) : valA > valB ? (sortOrder === 'asc' ? 1 : -1) : 0;
  });

  const totalPages = Math.ceil(sorted.length / itemsPerPage);
  const currentUsers = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);


  const toggleUserStatus = async (id, isActive) => {
    try {
      await axios.patch(`/users/${id}`, { is_active: !isActive });
      setMessage(`✅ User ${isActive ? 'deactivated' : 'activated'} successfully`);
      setShowMessagePopup(true);
      fetchUsers();
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.error || err.message));
      setShowMessagePopup(true);
    }
  };

  // Permission-based: system admins, or anyone with a user-management permission.
  const isCurrentUserAdmin = () => {
    if (!currentUser) return false;
    if (currentUser.is_system_admin === true) return true;
    const perms = Array.isArray(currentUser.permissions) ? currentUser.permissions : [];
    return perms.includes('users.update') || perms.includes('users.create') ||
           perms.includes('users.delete') || perms.includes('users.toggle');
  };

  // Organisation users only manage their own organisation's users (the API
  // enforces this): the organisation is fixed and the Admin role is not offered.
  const orgScoped = !!currentUser && currentUser.is_system_admin !== true && !!currentUser.organisation_id;

  const toggleRole = (roleName) => {
    setFormData(prev => {
      const roles = prev.roles.includes(roleName)
        ? prev.roles.filter(r => r !== roleName)
        : [...prev.roles, roleName];
      return { ...prev, roles };
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setShowMessagePopup(false);

    if (formData.roles.length === 0) {
      setMessage('❌ Please select at least one role');
      setShowMessagePopup(true);
      return;
    }

    try {
      const url = editMode
        ? `/users/${editId}`
        : '/users';

      const method = editMode ? 'put' : 'post';

      if (!editMode) {
        setIsAddingUser(true);
      }

      const payload = {
        ...formData,
        organisation_id: formData.organisation_id ? Number(formData.organisation_id) : null
      };
      await axios[method](url, payload);

      if (!editMode) {
        setIsAddingUser(false);
      }
      setMessage(editMode ? '✅ User updated successfully' : '✅ User added successfully');
      setShowMessagePopup(true);
      resetForm();
      fetchUsers();
    } catch (err) {
      if (!editMode) {
        setIsAddingUser(false);
      }
      setMessage('❌ ' + (err.response?.data?.error || err.message));
      setShowMessagePopup(true);
    }
  };

  const handleEdit = (u) => {
    const org = organisations.find(o => o.name === u.organisation);
    setFormData({
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      password: '',
      roles: u.roles || [],
      organisation_id: org ? org.id : ''
    });
    setEditMode(true);
    setEditId(u.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ first_name: '', last_name: '', email: '', password: '', roles: [], organisation_id: '' });
    setEditMode(false);
    setEditId(null);
    setShowForm(false);
  };

  const flattenedForExport = users.map(u => ({
    'First Name': u.first_name,
    'Last Name': u.last_name,
    Email: u.email,
    Organisation: u.organisation || '',
    Roles: (u.roles || []).join(', '),
    Status: u.is_active ? 'Active' : 'Inactive',
    'Created At': formatDateTime(u.created_at)
  }));

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(flattenedForExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'users.xlsx');
  };

  const exportPDF = () => exportBrandedPdf({
    title: 'USERS — REPORT',
    searchValue: 'All users',
    fileName: 'Users_Report',
    columns: [
      { header: 'First Name', width: 12, bold: true }, { header: 'Last Name', width: 12 },
      { header: 'Email', width: 20 }, { header: 'Organisation', width: 15 }, { header: 'Roles', width: 16 },
      { header: 'Status', width: 8 }, { header: 'Created At', width: 13 }
    ],
    rows: flattenedForExport.map(o => Object.values(o))
  });

  return (
    <div className="page-container">
      <div className="table-controls">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />
        <div className="button-group">
          <button className="export-button" onClick={() => {
            setEditMode(false);
            setEditId(null);
            setShowForm(true);
            setFormData({ first_name: '', last_name: '', email: '', password: '', roles: [], organisation_id: '' });
          }}>
            <FaPlus style={{ marginRight: 5 }} /> Add User
          </button>
          <CSVLink filename="users.csv" data={flattenedForExport} className="export-button">Export CSV</CSVLink>
          <button className="export-button" onClick={exportExcel}>Export Excel</button>
          <button className="export-button" onClick={exportPDF}>Export PDF</button>
        </div>
      </div>

      {showForm && (
        <form className="table-container" onSubmit={handleFormSubmit}>
          <h3>{editMode ? 'Edit User' : 'Add New User'}</h3>
          <div className="form-group name-group">
            <input placeholder="First Name" required value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} />
            <input placeholder="Last Name" required value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} />
            <input
              type="email"
              placeholder="Email"
              required
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              disabled={editMode && !isCurrentUserAdmin()}
            />
          </div>

          {editMode && (
            <div className="form-group">
              <input type="password" placeholder="New Password (optional)" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} />
            </div>
          )}

          <div className="form-group">
            <label>Organisation:</label>
            <select
              value={orgScoped ? currentUser.organisation_id : formData.organisation_id}
              disabled={orgScoped}
              onChange={e => setFormData({ ...formData, organisation_id: e.target.value })}
            >
              <option value="">-- Select Organisation --</option>
              {organisations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Assign Roles:</label>
            <div className="role-cards-container">
              {roles
                .filter(r => r.is_active)
                .filter(r => {
                  if (isCurrentUserAdmin() && !orgScoped) return true;
                  // Organisation users (and non-admins) only give OrgManager / OrgUser
                  return r.name === 'OrgManager' || r.name === 'OrgUser';
                })
                .map(r => (
                  <div 
                    key={r.id} 
                    className={`role-card ${formData.roles.includes(r.name) ? 'selected' : ''}`}
                    onClick={() => toggleRole(r.name)}
                  >
                    <div className="role-card-header">
                      <input
                        type="checkbox"
                        checked={formData.roles.includes(r.name)}
                        onChange={() => toggleRole(r.name)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="role-name">{r.name}</span>
                    </div>
                    {r.description && (
                      <p className="role-description">{r.description}</p>
                    )}
                  </div>
                ))}
            </div>
          </div>

          <button type="submit" className="export-button" disabled={isAddingUser}>{editMode ? 'Update' : 'Submit'}</button>
          <button type="button" className="btn-cancel" onClick={resetForm} disabled={isAddingUser}>Cancel</button>
        </form>
      )}

      {isAddingUser && (
        <div className="message-popup">
          <div className="message-popup-content">
            <p>Adding user...</p>
          </div>
        </div>
      )}

      {showMessagePopup && (
        <div className="message-popup">
          <div className="message-popup-content">
            <p>{message}</p>
            <button className="close-popup-button" onClick={() => setShowMessagePopup(false)}>Close</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1rem' }}>
        Showing {currentUsers.length} of {filtered.length} users {searchTerm ? `(filtered from ${users.length})` : ''}
      </div>

      <div className="table-container">
        <table className="pips-table users-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => handleSort('first_name')}>First Name</th>
              <th onClick={() => handleSort('last_name')}>Last Name</th>
              <th onClick={() => handleSort('email')}>Email</th>
              <th>Organisation</th>
              <th>Roles</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((u, idx) => (
              <tr key={u.id}>
                <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                <td>{u.first_name}</td>
                <td>{u.last_name}</td>
                <td>{u.email}</td>
                <td>{u.organisation || '-'}</td>
                <td>{u.roles?.join(', ') || '-'}</td>
                <td>{u.is_active ? 'Active' : 'Inactive'}</td>
                <td>
                  <button title="Edit" className="action-button" onClick={() => handleEdit(u)}><FaEdit /></button>
                  <button title={u.is_active ? 'Disable' : 'Enable'} className="action-button" onClick={() => setConfirmToggle({ show: true, userId: u.id, isActive: u.is_active })}>
                    {u.is_active ? <FaToggleOn className="toggle-icon--on" /> : <FaToggleOff className="toggle-icon--off" />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={sorted.length}
        itemsPerPage={itemsPerPage}
        itemLabel="user"
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
      />

      {/* Confirmation Popup for Activate/Deactivate */}
      {confirmToggle.show && (
        <div className="message-popup">
          <div className="message-popup-content">
            <p>{confirmToggle.isActive ? 'Are you sure you want to deactivate this user?' : 'Are you sure you want to activate this user?'}</p>
            <button
              className="export-button"
              onClick={async () => {
                await toggleUserStatus(confirmToggle.userId, confirmToggle.isActive);
                setConfirmToggle({ show: false, userId: null, isActive: null });
              }}
            >
              Yes
            </button>
            <button
              className="btn-cancel"
              onClick={() => setConfirmToggle({ show: false, userId: null, isActive: null })}
            >
              No
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageUsers;
